import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { memo, type ReactNode } from 'react';
import Toggle from '/@/components/forms/toggle';
import ToggleGroup from '/@/components/forms/toggle-group';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { PHENOLOGY_OPTIONS } from './phenology-options';

export const SIDEBAR_WIDTH = '24vw';

type DesktopSidebarProps = {
    trialTypesOptions: { id: string | number; name: string }[] | undefined;
    trialTypeToggleOptions: { value: string | number; label: string }[];
    selectedTypes: (string | number)[];
    onTrialTypeChange: (types: (string | number)[]) => void;
    selectedPhenologies: string[];
    onPhenologyChange: (phenologies: string[]) => void;
    campaignOptions: { value: string; label: string }[];
    selectedCampaign: string[];
    onCampaignChange: (campaign: string[]) => void;
    includeFields: boolean;
    onIncludeFieldsChange: (value: boolean) => void;
    onClearFilters: () => void;
    hasActiveFilters: boolean;
};

const FilterSection = ({ title, children }: { title: string; children: ReactNode }) => (
    <Stack spacing={1.5}>
        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
            {title}
        </Typography>
        {children}
    </Stack>
);

const DesktopSidebar = memo(function DesktopSidebar({
    trialTypesOptions,
    trialTypeToggleOptions,
    selectedTypes,
    onTrialTypeChange,
    selectedPhenologies,
    onPhenologyChange,
    campaignOptions,
    selectedCampaign,
    onCampaignChange,
    includeFields,
    onIncludeFieldsChange,
    onClearFilters,
    hasActiveFilters,
}: DesktopSidebarProps) {
    return (
        <Stack
            component='aside'
            width={SIDEBAR_WIDTH}
            flexShrink={0}
            bgcolor='background.paper'
            borderRight={(t) => `2px solid ${t.palette.divider}`}
            zIndex={1}
            p='3.2rem'
            height='100%'
            overflow='auto'
            spacing={3}
        >
            <Stack spacing={1}>
                <Typography variant='h6' color='primary'>
                    {_('Red de ensayos')}
                </Typography>
                <Typography variant='body2' color='primary'>
                    {_(
                        'A continuación podrás acceder a las ubicaciones de todos los ensayos de nuestra red. Utilizá los siguientes filtros para especificar tu búsqueda.'
                    )}
                </Typography>
            </Stack>

            <FilterSection title={_('Filtrá por tipo de ensayo')}>
                {trialTypesOptions ? (
                    <ToggleGroup
                        type='checkbox'
                        theme={settings.app.theme}
                        max={trialTypesOptions.length}
                        options={trialTypeToggleOptions}
                        value={selectedTypes as never}
                        onChange={onTrialTypeChange}
                        direction
                    />
                ) : (
                    <Skeleton variant='rectangular' width='100%' height={60} />
                )}
            </FilterSection>

            <FilterSection title={_('Filtrá por estadio fenológico')}>
                <ToggleGroup
                    theme={settings.app.theme}
                    type='checkbox'
                    max={PHENOLOGY_OPTIONS.length}
                    options={PHENOLOGY_OPTIONS}
                    value={selectedPhenologies as never}
                    onChange={onPhenologyChange}
                    direction
                />
            </FilterSection>

            <FilterSection title={_('Filtrá por año')}>
                <ToggleGroup
                    theme={settings.app.theme}
                    type='checkbox'
                    max={campaignOptions.length}
                    options={campaignOptions}
                    value={selectedCampaign as never}
                    onChange={onCampaignChange}
                    direction
                />
            </FilterSection>

            {/* Clears the three trial filters back to empty (= show all); leaves the fields toggle alone. */}
            <Stack alignItems='flex-start'>
                <Button variant='text' color='primary' onClick={onClearFilters} disabled={!hasActiveFilters}>
                    {_('Limpiar')}
                </Button>
            </Stack>

            <FilterSection title={_('Visualizá tus lotes en el mapa')}>
                <Toggle
                    type='checkbox'
                    theme={settings.app.theme}
                    label={includeFields ? _('Ocultar') : _('Mostrar')}
                    value={includeFields}
                    onChange={onIncludeFieldsChange}
                />
            </FilterSection>
        </Stack>
    );
});

export default DesktopSidebar;
